import { useState, useEffect } from "react";
import {
  Box, Flex, Text, Select, Button, Badge, Table, Thead, Tbody, Tr, Th, Td,
  TableContainer, HStack, VStack, Divider, Show, Hide, Modal, ModalOverlay,
  ModalContent, ModalHeader, ModalBody, ModalCloseButton, Image, Spinner,
  Center, useDisclosure, SimpleGrid,
} from "@chakra-ui/react";
import { ViewIcon, ChevronLeftIcon, ChevronRightIcon } from "@chakra-ui/icons";

const PAGE_SIZE = 5;
const RED = "#C0392B";
const LIGHT_RED = "#FFF5F5";
const BORDER = "#EBEBEB";
const ITERATIONS = [58, 59, 60, 61];

const generatePresignedUrl = async (s3Path) => {
  const endpoint = `https://s3service.sconexsoft.com/s3bucket/presigned-url?fileName=${encodeURIComponent(s3Path)}&bucketName=imageinsightimagesbeverages&operation=download`;
  try {
    const res = await fetch(endpoint);
    if (!res.ok) throw new Error("Failed to fetch presigned URL");
    const url = await res.text();
    return url.trim();
  } catch (err) {
    console.error("Failed to generate presigned URL:", err);
    return null;
  }
};

const classColorScheme = (cls) => {
  if (!cls) return { scheme: "gray", color: "#718096", bg: "#F7FAFC" };
  const c = cls.toUpperCase();
  if (c === "GOLD") return { color: "#B7791F", bg: "#FEFCE8" };
  if (c === "SILVER") return { color: "#718096", bg: "#EDF2F7" };
  if (c === "BRONZE") return { color: "#C05621", bg: "#FFFAF0" };
  return { color: "#3182CE", bg: "#EBF8FF" };
};

function StoreModal({ store, isOpen, onClose }) {
  const [actualUrl, setActualUrl] = useState(null);
  const [annotatedUrl, setAnnotatedUrl] = useState(null);
  const [imgLoading, setImgLoading] = useState(true);

  useEffect(() => {
    if (!store || !isOpen) return;
    const loadImages = async () => {
      setImgLoading(true);
      setActualUrl(null);
      setAnnotatedUrl(null);
      const [actual, annotated] = await Promise.all([
        generatePresignedUrl(store.imageFileName),
        generatePresignedUrl(store.s3PathAnnotatedFile),
      ]);
      setActualUrl(actual);
      setAnnotatedUrl(annotated);
      setImgLoading(false);
    };
    loadImages();
  }, [store, isOpen]);

  const images = [
    { label: "Actual Image", src: actualUrl, name: store?.imageFileName },
    { label: "Annotated Image", src: annotatedUrl, name: store?.s3PathAnnotatedFile },
  ];

  const cs = classColorScheme(store?.storeCls);

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="4xl" isCentered scrollBehavior="inside">
      <ModalOverlay bg="blackAlpha.600" backdropFilter="blur(4px)" />
      <ModalContent borderRadius="14px" overflow="hidden">

        {/* Header */}
        <ModalHeader bg={RED} color="white" py={4} px={6}>
          <Text fontWeight="700" fontSize="md">{store?.storeName}</Text>
          <HStack mt={2} spacing={2} flexWrap="wrap">
            {[`ID: ${store?.storeId}`, store?.storeType, store?.storeCls].map((label) => (
              <Badge
                key={label}
                bg="whiteAlpha.300"
                color="white"
                fontSize="11px"
                px={2}
                py={0.5}
                borderRadius="4px"
                fontWeight="600"
              >
                {label}
              </Badge>
            ))}
          </HStack>
        </ModalHeader>
        <ModalCloseButton color="white" top={4} right={4} />

        {/* Body */}
        <ModalBody p={6}>
          {imgLoading ? (
            <Center py={12}>
              <VStack spacing={3}>
                <Spinner color={RED} size="lg" thickness="3px" />
                <Text fontSize="sm" color="gray.400">Loading images...</Text>
              </VStack>
            </Center>
          ) : (
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
              {images.map(({ label, src, name }) => (
                <Box key={label}>
                  <Text
                    fontSize="11px"
                    fontWeight="700"
                    letterSpacing="0.8px"
                    color="gray.500"
                    textTransform="uppercase"
                    mb={3}
                  >
                    {label}
                  </Text>
                  <Box
                    borderRadius="10px"
                    overflow="hidden"
                    border={`1px solid ${BORDER}`}
                    bg="gray.50"
                    h="270px"
                  >
                    {src ? (
                      <Image
                        src={src}
                        alt={label}
                        w="100%"
                        h="100%"
                        objectFit="cover"
                        fallback={
                          <Center h="270px" bg="gray.100" flexDirection="column" gap={2}>
                            <Text fontSize="sm" color="gray.400">Image not available</Text>
                            <Text fontSize="10px" color="gray.300" px={4} textAlign="center" wordBreak="break-all">
                              {name}
                            </Text>
                          </Center>
                        }
                      />
                    ) : (
                      <Center h="270px" bg="gray.100" flexDirection="column" gap={2}>
                        <Text fontSize="sm" color="gray.400">Image not available</Text>
                        <Text fontSize="10px" color="gray.300" px={4} textAlign="center" wordBreak="break-all">
                          {name}
                        </Text>
                      </Center>
                    )}
                  </Box>
                </Box>
              ))}
            </SimpleGrid>
          )}
        </ModalBody>

      </ModalContent>
    </Modal>
  );
}

export default function StoreManager() {
  const [selectedIteration, setSelectedIteration] = useState("");
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedStore, setSelectedStore] = useState(null);
  const { isOpen, onOpen, onClose } = useDisclosure();

  const totalPages = Math.ceil(stores.length / PAGE_SIZE);
  const startIdx = (currentPage - 1) * PAGE_SIZE;
  const pageItems = stores.slice(startIdx, startIdx + PAGE_SIZE);

  const handleIterationChange = async (e) => {
    const val = e.target.value;
    setSelectedIteration(val);
    setCurrentPage(1);
    setStores([]);
    setError("");
    if (!val) return;
    setLoading(true);
    try {
      const res = await fetch(
        `https://orgi-dev-api.sconexsoft.com/orgi/api/sku-prediction/stores?iterationId=${val}`
      );
      if (!res.ok) throw new Error();
      setStores(await res.json());
    } catch {
      setError("Failed to load stores. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = (store) => {
    setSelectedStore(store);
    onOpen();
  };

  return (
    <Box minH="100vh" bg="gray.50" py={{ base: 6, md: 10 }} px={{ base: 3, sm: 4, md: 8 }}
      fontFamily="'Segoe UI', sans-serif">

      {/* Dropdown */}
      <Flex align={{ base: "flex-start", sm: "center" }} direction={{ base: "column", sm: "row" }}
        gap={3} mb={8} mt={6}>
        <Text fontWeight="600" fontSize="sm" color="gray.600" whiteSpace="nowrap">
          Iteration ID:
        </Text>
        <Select
          placeholder="-- Select Iteration --"
          value={selectedIteration}
          onChange={handleIterationChange}
          maxW={{ base: "100%", sm: "260px" }}
          borderColor={BORDER}
          borderRadius="8px"
          fontSize="sm"
          fontWeight="500"
          focusBorderColor={RED}
          _hover={{ borderColor: RED }}
          bg="white"
          size="md"
        >
          {ITERATIONS.map((id) => (
            <option key={id} value={id}>{id}</option>
          ))}
        </Select>
      </Flex>

      {/* Table Card */}
      <Box
        bg="white"
        borderRadius="12px"
        border={`1px solid ${BORDER}`}
        overflow="hidden"
        boxShadow="0 2px 12px rgba(0,0,0,0.06)"
      >
        {/* Loading */}
        {loading && (
          <Center py={16}>
            <VStack spacing={3}>
              <Spinner color={RED} size="lg" thickness="3px" />
              <Text fontSize="sm" color="gray.400">Loading stores...</Text>
            </VStack>
          </Center>
        )}

        {/* Error */}
        {!loading && error && (
          <Center py={16}>
            <Text fontSize="sm" color="red.500">{error}</Text>
          </Center>
        )}

        {/* Desktop Table */}
        {!loading && !error && (
          <Hide below="md">
            <TableContainer>
              <Table variant="simple" size="md">
                <Thead bg="gray.50">
                  <Tr>
                    {["STORE ID", "STORE NAME", "STORE TYPE", "STORE CLASS", "ACTIONS"].map((h) => (
                      <Th key={h} fontSize="11px" letterSpacing="0.8px" color="gray.500" borderColor={BORDER}>
                        {h}
                      </Th>
                    ))}
                  </Tr>
                </Thead>
                <Tbody>
                  {pageItems.length === 0 ? (
                    <Tr>
                      <Td colSpan={5} textAlign="center" py={16} color="gray.400" fontSize="sm">
                        {selectedIteration
                          ? "No stores found for this iteration."
                          : "Please select an Iteration ID to view stores."}
                      </Td>
                    </Tr>
                  ) : (
                    pageItems.map((store, i) => {
                      const cs = classColorScheme(store.storeCls);
                      return (
                        <Tr
                          key={store.storeId}
                          bg={i % 2 === 0 ? "white" : "gray.50"}
                          _hover={{ bg: LIGHT_RED }}
                          transition="background 0.15s"
                        >
                          <Td fontSize="sm" fontWeight="500" color="gray.600" borderColor={BORDER}>
                            {store.storeId}
                          </Td>
                          <Td fontSize="sm" fontWeight="600" color="gray.800" borderColor={BORDER}>
                            {store.storeName}
                          </Td>
                          <Td borderColor={BORDER}>
                            <Badge
                              px={2} py={0.5} borderRadius="4px" fontSize="11px"
                              fontWeight="700" bg="#EBF8FF" color="#3182CE"
                            >
                              {store.storeType}
                            </Badge>
                          </Td>
                          <Td borderColor={BORDER}>
                            <Badge
                              px={2} py={0.5} borderRadius="4px" fontSize="11px"
                              fontWeight="700" bg={cs.bg} color={cs.color}
                            >
                              {store.storeCls}
                            </Badge>
                          </Td>
                          <Td borderColor={BORDER}>
                            <Button
                              size="sm"
                              variant="outline"
                              borderColor={RED}
                              color={RED}
                              leftIcon={<ViewIcon />}
                              _hover={{ bg: RED, color: "white" }}
                              transition="all 0.15s"
                              fontWeight="600"
                              borderRadius="8px"
                              onClick={() => handleViewDetails(store)}
                            >
                              View Details
                            </Button>
                          </Td>
                        </Tr>
                      );
                    })
                  )}
                </Tbody>
              </Table>
            </TableContainer>
          </Hide>
        )}

        {/* Mobile Cards */}
        {!loading && !error && (
          <Show below="md">
            {pageItems.length === 0 ? (
              <Box textAlign="center" py={16} color="gray.400" fontSize="sm">
                {selectedIteration
                  ? "No stores found for this iteration."
                  : "Please select an Iteration ID to view stores."}
              </Box>
            ) : (
              <VStack spacing={0} divider={<Divider borderColor={BORDER} />}>
                {pageItems.map((store) => {
                  const cs = classColorScheme(store.storeCls);
                  return (
                    <Box
                      key={store.storeId}
                      w="100%"
                      px={4} py={4}
                      _hover={{ bg: LIGHT_RED }}
                      transition="background 0.15s"
                    >
                      <Flex align="center" justify="space-between" gap={3}>
                        <VStack align="flex-start" spacing={1} minW={0} flex={1}>
                          <Text fontSize="sm" fontWeight="700" color="gray.800" noOfLines={1}>
                            {store.storeName}
                          </Text>
                          <HStack spacing={2} flexWrap="wrap">
                            <Badge fontSize="11px" colorScheme="red" variant="subtle" borderRadius="4px" px={2}>
                              {store.storeId}
                            </Badge>
                            <Badge fontSize="11px" bg="#EBF8FF" color="#3182CE" borderRadius="4px" px={2}>
                              {store.storeType}
                            </Badge>
                            <Badge fontSize="11px" bg={cs.bg} color={cs.color} borderRadius="4px" px={2}>
                              {store.storeCls}
                            </Badge>
                          </HStack>
                        </VStack>
                        <Button
                          size="xs"
                          variant="outline"
                          borderColor={RED}
                          color={RED}
                          leftIcon={<ViewIcon boxSize={3} />}
                          _hover={{ bg: RED, color: "white" }}
                          transition="all 0.15s"
                          fontWeight="600"
                          borderRadius="6px"
                          flexShrink={0}
                          onClick={() => handleViewDetails(store)}
                        >
                          View
                        </Button>
                      </Flex>
                    </Box>
                  );
                })}
              </VStack>
            )}
          </Show>
        )}

        {/* Pagination */}
        {!loading && stores.length > 0 && (
          <Flex
            align="center"
            justify="space-between"
            px={{ base: 4, md: 6 }}
            py={4}
            borderTop={`1px solid ${BORDER}`}
            bg="gray.50"
            flexWrap="wrap"
            gap={3}
          >
            <Text fontSize="13px" color="gray.500">
              Showing {startIdx + 1}–{Math.min(startIdx + PAGE_SIZE, stores.length)} of {stores.length} stores
            </Text>
            <HStack spacing={2}>
              <Button
                size="sm" variant="outline" borderColor={BORDER} color="gray.600"
                leftIcon={<ChevronLeftIcon />}
                isDisabled={currentPage === 1}
                onClick={() => setCurrentPage((p) => p - 1)}
                _hover={{ borderColor: RED, color: RED }}
                borderRadius="6px" fontSize="13px"
              >
                Previous
              </Button>
              <HStack spacing={1}>
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                  <Button
                    key={p} size="sm"
                    onClick={() => setCurrentPage(p)}
                    bg={p === currentPage ? RED : "white"}
                    color={p === currentPage ? "white" : "gray.600"}
                    border="1px solid"
                    borderColor={p === currentPage ? RED : BORDER}
                    _hover={{ borderColor: RED, color: p === currentPage ? "white" : RED }}
                    borderRadius="6px" fontWeight="600" minW="36px" fontSize="13px"
                  >
                    {p}
                  </Button>
                ))}
              </HStack>
              <Button
                size="sm" variant="outline" borderColor={BORDER} color="gray.600"
                rightIcon={<ChevronRightIcon />}
                isDisabled={currentPage === totalPages}
                onClick={() => setCurrentPage((p) => p + 1)}
                _hover={{ borderColor: RED, color: RED }}
                borderRadius="6px" fontSize="13px"
              >
                Next
              </Button>
            </HStack>
          </Flex>
        )}
      </Box>

      {/* Modal */}
      <StoreModal store={selectedStore} isOpen={isOpen} onClose={onClose} />
    </Box>
  );
}